import { DataStore } from 'aws-amplify';
import { Location, Printer, UserDetails, UserProfile } from '../models';
import { captureError } from '../common/sentry';
import { LocationProps, convertLocationToProps } from './locations';
import { PrinterProps, convertPrinterToProps } from './printers';

export interface UserDetailsProps {
  id: string;
  cognitoID: string;
  email?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  adminPanelAccess?: boolean | null;
  location?: LocationProps | null;
  printer?: PrinterProps | null;
}

interface UpdateAdminAccessParams {
  cognitoID: string;
  adminPanelAccess: boolean;
}

export interface UserProfileProps {
  cognitoID: string;
  email?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  adminPanelAccess?: boolean | null;
}

export interface UserDetailsDataProps {
  get: (id: string, cognitoUser: any) => Promise<UserDetailsProps>;
  getAll: () => Promise<UserDetailsProps[]>;
  getAllProfiles: () => Promise<UserProfileProps[]>;
  update: (data: UserDetailsProps) => Promise<UserDetailsProps>;
  getOriginalUserDetails: (id: string) => Promise<UserDetails | undefined>;
  updateAdminPanelAccess: ({ cognitoID, adminPanelAccess }: UpdateAdminAccessParams) => Promise<UserProfileProps>;
}

let createInFlight: Promise<UserDetails | null> | null = null;

export const userDetailsData = () => {
  async function get(cognitoID: string, cognitoUser: any): Promise<UserDetailsProps> {
    const details = await DataStore.query(UserDetails, (u) => u.cognitoID.eq(cognitoID));
    if (details.length) {
      const userDetails = details[0];
      if (userDetails) {
        const cognitoUserEmail = cognitoUser?.attributes?.email || null;
        const cognitoUserFirstname = cognitoUser?.attributes?.given_name || null;
        const cognitoUserLastname = cognitoUser?.attributes?.family_name || null;

        // Get or create UserProfile
        let profile = await getOrCreateUserProfile(
          cognitoID,
          cognitoUserEmail,
          cognitoUserFirstname,
          cognitoUserLastname
        );

        // Sync cognito attributes to UserProfile if changed
        if (
          (cognitoUserEmail && !profile.email) ||
          (cognitoUserFirstname && !profile.firstname) ||
          (cognitoUserLastname && !profile.lastname) ||
          (profile.email && cognitoUserEmail && profile.email !== cognitoUserEmail) ||
          (profile.firstname && cognitoUserFirstname && profile.firstname !== cognitoUserFirstname) ||
          (profile.lastname && cognitoUserLastname && profile.lastname !== cognitoUserLastname)
        ) {
          profile = await updateUserProfile(cognitoID, {
            email: cognitoUserEmail,
            firstname: cognitoUserFirstname,
            lastname: cognitoUserLastname
          });
        }

        return await convertToProps(userDetails, profile);
      }
    } else if (!createInFlight) {
      createInFlight = (async () => {
        const again = await DataStore.query(UserDetails, (u) => u.cognitoID.eq(cognitoID));

        if (again?.length) {
          return again[0];
        }

        return await DataStore.save(
          new UserDetails({
            cognitoID,
            location: null,
            printer: null
          })
        );
      })();

      const createdOrFound = await createInFlight;
      createInFlight = null;

      if (!createdOrFound) {
        return {} as UserDetailsProps;
      }

      // Also create UserProfile
      const cognitoUserEmail = cognitoUser?.attributes?.email ?? null;
      const cognitoUserFirstname = cognitoUser?.attributes?.given_name ?? null;
      const cognitoUserLastname = cognitoUser?.attributes?.family_name ?? null;
      const profile = await getOrCreateUserProfile(
        cognitoID,
        cognitoUserEmail,
        cognitoUserFirstname,
        cognitoUserLastname
      );

      return convertToProps(createdOrFound, profile);
    }
    return {} as UserDetailsProps;
  }

  async function getAll() {
    const details = await DataStore.query(UserDetails);
    if (details.length) {
      const mappedData = await Promise.all(
        details.map(async (detail) => {
          const profile = await getUserProfile(detail.cognitoID);
          return convertToProps(detail, profile);
        })
      );
      return mappedData;
    }
    return [];
  }

  async function update(data: UserDetailsProps) {
    try {
      const original = await getOriginalUserDetails(data.id);
      const locationToUpdate = data.location ? await DataStore.query(Location, data.location.id) : null;
      const printerToUpdate = data.printer ? await DataStore.query(Printer, data.printer.id) : null;
      if (original) {
        const updated = await DataStore.save(
          UserDetails.copyOf(original, (updated) => {
            updated.location = locationToUpdate;
            updated.printer = printerToUpdate;
          })
        );

        // Update UserProfile for email/firstname/lastname/adminPanelAccess
        const profile = await updateUserProfile(original.cognitoID, {
          email: data.email,
          firstname: data.firstname,
          lastname: data.lastname,
          adminPanelAccess: data.adminPanelAccess
        });

        if (updated) {
          return await convertToProps(updated, profile);
        }
      }
      return {} as UserDetailsProps;
    } catch (error) {
      console.error('Error updating UserDetails:', error);
      captureError(error, `userDetails-update:${data.id}`);
      throw error;
    }
  }

  async function getAllProfiles(): Promise<UserProfileProps[]> {
    const profiles = await DataStore.query(UserProfile);
    return profiles.map((p) => ({
      cognitoID: p.cognitoID,
      email: p.email ?? null,
      firstname: p.firstname ?? null,
      lastname: p.lastname ?? null,
      adminPanelAccess: p.adminPanelAccess ?? null
    }));
  }

  async function updateAdminPanelAccess({
    cognitoID,
    adminPanelAccess
  }: UpdateAdminAccessParams): Promise<UserProfileProps> {
    try {
      const profile = await updateUserProfile(cognitoID, { adminPanelAccess });

      return {
        cognitoID: profile.cognitoID,
        email: profile.email ?? null,
        firstname: profile.firstname ?? null,
        lastname: profile.lastname ?? null,
        adminPanelAccess: profile.adminPanelAccess ?? null
      };
    } catch (error) {
      console.error('Error updating admin panel access:', error);
      captureError(error, `userDetails-updateAdminPanelAccess:${cognitoID}`);
      throw error;
    }
  }

  async function getOriginalUserDetails(id: string) {
    return await DataStore.query(UserDetails, id);
  }

  // UserProfile helpers

  async function getUserProfile(cognitoID: string): Promise<UserProfile | null> {
    try {
      const profile = await DataStore.query(UserProfile, cognitoID);
      return profile || null;
    } catch {
      return null;
    }
  }

  async function getOrCreateUserProfile(
    cognitoID: string,
    email?: string | null,
    firstname?: string | null,
    lastname?: string | null
  ): Promise<UserProfile> {
    const existing = await getUserProfile(cognitoID);
    if (existing) return existing;

    return await DataStore.save(
      new UserProfile({
        cognitoID,
        email: email ?? null,
        firstname: firstname ?? null,
        lastname: lastname ?? null,
        adminPanelAccess: false
      })
    );
  }

  async function updateUserProfile(
    cognitoID: string,
    updates: {
      email?: string | null;
      firstname?: string | null;
      lastname?: string | null;
      adminPanelAccess?: boolean | null;
    }
  ): Promise<UserProfile> {
    const original = await getUserProfile(cognitoID);
    if (original) {
      return await DataStore.save(
        UserProfile.copyOf(original, (draft) => {
          if (updates.email !== undefined) draft.email = updates.email;
          if (updates.firstname !== undefined) draft.firstname = updates.firstname;
          if (updates.lastname !== undefined) draft.lastname = updates.lastname;
          if (updates.adminPanelAccess !== undefined) draft.adminPanelAccess = updates.adminPanelAccess;
        })
      );
    }
    // If no profile exists, create one
    return await DataStore.save(
      new UserProfile({
        cognitoID,
        email: updates.email ?? null,
        firstname: updates.firstname ?? null,
        lastname: updates.lastname ?? null,
        adminPanelAccess: updates.adminPanelAccess ?? false
      })
    );
  }

  async function convertToProps(data: UserDetails, profile?: UserProfile | null) {
    const location = await data.location;
    const printer = await data.printer;

    const results: UserDetailsProps = {
      id: data.id,
      cognitoID: data.cognitoID,
      email: profile?.email ?? null,
      firstname: profile?.firstname ?? null,
      lastname: profile?.lastname ?? null,
      adminPanelAccess: profile?.adminPanelAccess ?? null,
      location: location ? await convertLocationToProps(location) : null,
      printer: printer ? convertPrinterToProps(printer) : null
    };

    return results;
  }

  const returned: UserDetailsDataProps = {
    get,
    getAll,
    getAllProfiles,
    update,
    getOriginalUserDetails,
    updateAdminPanelAccess
  };

  return returned;
};
